import { Box, Typography } from "@mui/material";
import SchedulingBar from "./SchedulingBar";
import styles from "../priestSchedule.module.scss";
import {
  CommonButton,
  CommonOutlinedButton,
} from "../../../common/StyledComponents/StyledFields";
import { useEffect, useRef, useState } from "react";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
  calculateOffsetDate,
  getDatesBetween,
  getDevoteeId,
  getDayOfWeek,
  calculateDuration,
} from "../../../../infrastructure/backendService";
import apiPath from "../../../../config/apiPaths.json";
import toast from "react-hot-toast";
import ErrorPopup from "../../../common/ErrorPopup";
import constants from "../../../../config/constants.json";
import PriestScheduleDialog from "../PriestScheduleDialog";
import SuccessPopup from "../../../devotee/donation/successPopup";
import { useAppSelector } from "../../../../app/hook";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

interface SchedulingScreenProps {
  formik: any;
  setShowPriestSchedulingScreen?: (show: boolean) => void;
}

const SchedulingScreen = (props: SchedulingScreenProps) => {
  const componentRef = useRef<any>(null);
  const { formik, setShowPriestSchedulingScreen } = props;
  const { poojaDetails } = useAppSelector((state) => state.poojaBookingDS);
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [schedulePeriod, setSchedulePeriod] = useState({
    startDate: new Date(formik?.values?.date),
    endDate: calculateOffsetDate(new Date(formik?.values?.date)),
  });
  const [priestWeeklySchedule, setPriestWeeklySchedule] = useState<any>(null);
  const [weeklySchedules, setWeeklySchedules] = useState<any>(null);
  const [schedulePriestData, setSchedulePriestData] = useState<any>(formik);
  const [isModifyOrDelete, setIsModifyOrDelete] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState(false);
  const [scheduledPoojaData, setScheduledPoojaData] = useState<any>(null);
  const [isPriestScheduled, setIsPriestScheduled] = useState<any>(false);
  const location = useLocation();

  const navigate = useNavigate();

  const setWeeklySchedulesState = (data: any) => {
    if (!formik?.values?.allPriests) {
      const schedulesByDate = data?.[0]?.schedulesByDate;

      const dateWithDay = getDatesBetween(
        schedulePeriod.startDate,
        schedulePeriod.endDate
      );
      // Now, create a new array of filtered schedules by date
      const schedules = dateWithDay.map((date: any) => {
        return {
          date,
          priestOrHallName: formik?.values?.priestName,
          priestOrHallId: formik?.values?.priestId,
          schedules: schedulesByDate?.filter(
            (item: any) => item.date === date?.date
          ),
        };
      });
      setWeeklySchedules(schedules);
    } else {
      const schedules = data?.map((each: any) => {
        return {
          ...each,
          date: { date: each?.startDate },
          schedules: each?.schedulesByDate,
        };
      });
      setWeeklySchedules(schedules);
    }
  };

  const scheduleApiCall = async () => {
    await apiRequest({
      method: "GET",
      endpoint: apiPath.schedules,
      params: {
        scheduleType: "PRIEST",
        schedulesForDay: formik?.values?.allPriests,
        priestId: formik?.values?.priestId,
        startDate: displayDateFormat(schedulePeriod?.startDate),
        endDate: displayDateFormat(schedulePeriod?.endDate),
      },
      includeToken: true,
    })
      .then((res) => {
        setPriestWeeklySchedule(res.data);
        setWeeklySchedulesState(res.data);
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  useEffect(() => {
    setSchedulePeriod({
      startDate: new Date(formik?.values?.date),
      endDate: calculateOffsetDate(new Date(formik?.values?.date)),
    });
  }, [formik]);

  useEffect(() => {
    if (schedulePeriod.startDate && schedulePeriod.endDate) {
      scheduleApiCall();
    }
  }, [schedulePeriod, formik?.values?.allPriests]);

  const handlePreviousAndNextWeek = (isPrevious = false) => {
    let startDate = schedulePeriod.startDate;
    let endDate = schedulePeriod.endDate;
    if (isPrevious) {
      endDate = calculateOffsetDate(startDate, -1);
      startDate = calculateOffsetDate(startDate, -7);
    } else {
      startDate = calculateOffsetDate(endDate, 1);
      endDate = calculateOffsetDate(endDate, 7);
    }
    setSchedulePeriod({ startDate, endDate });
  };

  const handlePreviousAndNextDay = (isPrevious = false) => {
    let startDate = schedulePeriod.startDate;
    if (isPrevious) {
      startDate = calculateOffsetDate(startDate, -1);
    } else {
      startDate = calculateOffsetDate(startDate, 1);
    }
    setSchedulePeriod({ ...schedulePeriod, startDate });
  };

  const handleCloseScheduleDialog = () => {
    setOpenScheduleDialog(false);
  };

  const handleClickAdd = (pooja?: any) => {
    if (!isPriestScheduled || pooja.isModifyOrDelete) {
      setOpenScheduleDialog(true);
      const isDailySchedule = formik?.values?.allPriests;
      if (pooja.isModifyOrDelete) {
        const poojaLocation = !pooja.devoteeDetails
          ? "PRIEST_DUTIES"
          : pooja.outsidePooja
          ? "OUTSIDE_POOJA"
          : "TEMPLE_POOJA";
        const date = pooja.date;
        const priestName = isDailySchedule
          ? pooja.priestOrHallName
          : priestWeeklySchedule?.[0]?.priestOrHallName;
        const priestId = isDailySchedule
          ? pooja.priestOrHallId
          : formik?.values?.priestId;
        setSchedulePriestData({
          ...pooja,
          poojaLocation,
          date,
          priestId,
          priestName,
        });
        setIsModifyOrDelete(true);
      } else {
        const priestName = isDailySchedule
          ? pooja.priestOrHallName
          : formik?.values?.priestName;
        const priestId = isDailySchedule
          ? pooja.priestOrHallId
          : formik?.values?.priestId;
        setSchedulePriestData({
          ...formik.values,
          startTime: formik?.values?.time,
          date: pooja.date,
          priestName,
          priestId,
        });
        setIsModifyOrDelete(false);
      }
    }
  };

  const handleSchedulePriest = (poojaData: any) => {
    const payload = {
      description: poojaData?.description,
      startDate: displayDateFormat(new Date(poojaData?.date)),
      startTime: poojaData?.startTime,
      endTime: poojaData?.endTime,
      miles: 0,
      poojaId: poojaData?.poojaId,
      priestId: poojaData?.priestId,
      hallId: 0,
      devoteeId: poojaDetails?.devoteeID,
      scheduleType: "PRIEST",
      poojaHallRequestsId: poojaDetails?.poojaHallRequestsId,
      poojaLocation: poojaData?.poojaLocation,
      amount: poojaDetails?.poojaAmount,
    };
    apiRequest({
      method: "POST",
      endpoint: apiPath.schedules,
      includeToken: true,
      body: payload,
    })
      .then(() => {
        toast.custom(
          <SuccessPopup
            headerMessage="Add Schedule"
            showMessage={
              poojaDetails.isManagerRequest
                ? "Accepted your schedule. Please continue to Pooja Booking."
                : "Successfully Scheduled the Priest."
            }
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
        setIsPriestScheduled(true);
        if (poojaDetails.isManagerRequest) {
          const payload = {
            poojaId: poojaData?.poojaId,
            priestId: poojaData?.priestId,
            startTime: poojaData.startTime,
            startDate: poojaData?.date?.replaceAll("/", "-"),
            poojaLocation: poojaData?.poojaLocation,
            poojaAmount: poojaDetails?.poojaAmount,
            description: poojaData?.description,
            poojaMins: calculateDuration(
              poojaData.startTime,
              poojaData.endTime
            ),
            schedulePriest: false,
          };
          setScheduledPoojaData(payload);
        }
        handleCloseScheduleDialog();
        scheduleApiCall();
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };
  const handleUpdateSchedulePriest = (poojaDetails: any) => {
    const payload = {
      description: poojaDetails?.description,
      startDate: displayDateFormat(new Date(poojaDetails?.date)),
      startTime: poojaDetails?.startTime,
      endTime: poojaDetails?.endTime,
      miles: 0,
      poojaId: poojaDetails?.poojaId,
      hallId: 0,
      devoteeId:
        poojaDetails?.poojaLocation === "PRIEST_DUTIES"
          ? ""
          : poojaDetails?.devoteeId,
    };
    apiRequest({
      method: "PUT",
      endpoint: `${apiPath.schedules}/${poojaDetails?.scheduleId}`,
      includeToken: true,
      body: payload,
    })
      .then(() => {
        toast.custom(
          <SuccessPopup
            headerMessage="Update Schedule"
            showMessage="Successfully Updated the Schedule!"
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
        handleCloseScheduleDialog();
        scheduleApiCall();
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleDeletePriestSchedule = (scheduleId: string) => {
    apiRequest({
      method: "DELETE",
      endpoint: `${apiPath.schedules}/${scheduleId}`,
      includeToken: true,
    })
      .then(() => {
        toast.custom(
          <SuccessPopup
            headerMessage="Delete Schedule"
            showMessage="Successfully Deleted the Schedule!"
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
        handleCloseScheduleDialog();
        scheduleApiCall();
      })
      .catch((err) => {
        toast.custom(
          <ErrorPopup
            showMessage={catchErrorMessageHandler(err)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleBack = () => {
    if (poojaDetails.isManagerRequest) {
      if (scheduledPoojaData) {
        navigate(`${location.pathname}`, { state: scheduledPoojaData });
      } else {
        const payload = {
          poojaId: poojaDetails?.poojaId,
          priestId: poojaDetails?.priestId,
          startTime: poojaDetails?.poojaTime,
          startDate: poojaDetails?.dateOfPooja?.replaceAll("/", "-"),
          poojaLocation: poojaDetails?.poojaLocation,
          poojaAmount: poojaDetails?.poojaAmount,
          poojaMins: poojaDetails?.poojaDuration,
          schedulePriest: true,
        };
        navigate(`${location.pathname}`, { state: payload });
      }
    }
    setShowPriestSchedulingScreen?.(false);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const renderWeekAndDayButtons = () => {
    return (
      <Box
        px="15px"
        pt="24px"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
        }}
        className="no_print"
      >
        {!formik?.values?.allPriests ? (
          <Box>
            <CommonOutlinedButton
              className={styles.week_and_day_buttons}
              sx={{ mr: "10px" }}
              onClick={() => {
                handlePreviousAndNextWeek(true);
              }}
            >
              {"< Previous Week >"}
            </CommonOutlinedButton>
            <CommonOutlinedButton
              className={styles.week_and_day_buttons}
              onClick={() => handlePreviousAndNextWeek()}
            >
              {"< Next Week >"}
            </CommonOutlinedButton>
          </Box>
        ) : (
          <Box>
            <CommonOutlinedButton
              className={styles.week_and_day_buttons}
              sx={{ mr: "10px" }}
              onClick={() => {
                handlePreviousAndNextDay(true);
              }}
            >
              {"< Previous Day >"}
            </CommonOutlinedButton>
            <CommonOutlinedButton
              className={styles.week_and_day_buttons}
              onClick={() => handlePreviousAndNextDay()}
            >
              {"< Next Day >"}
            </CommonOutlinedButton>
          </Box>
        )}
        <Box>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={handlePrint}
            sx={{ mt: { xs: "15px", sm: "0px" } }}
          >
            Print Report
          </CommonOutlinedButton>
        </Box>
      </Box>
    );
  };

  const renderScheduling = () => {
    return (
      <Box px="15px" pb="24px">
        <Box
          sx={{ display: "flex", mt: "20px", py: "8px" }}
          className={styles.date_and_schedule_title_container}
        >
          <Typography
            className={styles.date_and_schedule_title}
            sx={{
              px: { xs: "5px", md: "8px" },
              fontSize: { xs: "12px", md: "16px" },
              width: { xs: "45px", md: "84px" },
            }}
          >
            {formik?.values?.allPriests ? "Priest Name" : "Dates"}
          </Typography>
          <Typography
            className={styles.date_and_schedule_title}
            sx={{ pl: "15px", fontSize: { xs: "12px", md: "16px" } }}
          >
            {formik?.values?.allPriests ? "Day Schedules" : "Weekly Schedules"}
          </Typography>
        </Box>
        {weeklySchedules?.map((pooja: any) => {
          return (
            <Box
              sx={{ display: "flex", height: "fit-content" }}
              className={styles.date_and_scheduling_bar_container}
            >
              <Typography
                sx={{
                  px: { xs: "5px", md: "8px" },
                  pt: "16px",
                  fontSize: { xs: "9px", md: "12px" },
                  width: { xs: "45px", md: "84px" },
                }}
                className={styles.date_text}
              >
                {formik?.values?.allPriests
                  ? `${pooja?.priestOrHallName}`
                  : `${pooja?.date?.day} ${pooja?.date?.date}`}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  px: "15px",
                  pt: "4px",
                  pb: "9px",
                  background: "#FFECB4",
                }}
              >
                <SchedulingBar
                  handleClickAdd={(details) =>
                    handleClickAdd({
                      ...details,
                      priestOrHallName: pooja?.priestOrHallName,
                      priestOrHallId: pooja?.priestOrHallId,
                    })
                  }
                  priestSchedulesData={pooja}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderButtons = () => {
    return (
      <Box mt="25px">
        <CommonButton
          onClick={handleBack}
          sx={{ textTransform: "none !important" }}
        >
          {poojaDetails.isManagerRequest
            ? "Continue to Pooja Booking"
            : "Back to Pooja/Hall Requests"}
        </CommonButton>
        {/* <CommonButton
          onClick={handleBack}
          disabled={!isPriestScheduled}
          sx={{ ml: "15px" }}
        >
          Submit
        </CommonButton> */}
      </Box>
    );
  };

  return (
    <>
      <Box className={styles.scheduling_container} ref={componentRef}>
        <Box className={styles.scheduling_title_container}>
          <Typography
            className={styles.scheduling_title}
            fontSize={{ xs: "15px", md: "23px" }}
          >
            {!formik?.values?.allPriests
              ? `Priest Weekly Schedule (Period ${displayDateFormat(
                  schedulePeriod?.startDate
                )} to ${displayDateFormat(schedulePeriod?.endDate)})`
              : `Priest Daily Schedule (${getDayOfWeek(
                  schedulePeriod?.startDate
                )} - ${displayDateFormat(schedulePeriod?.startDate)})`}
          </Typography>
        </Box>
        <>
          {renderWeekAndDayButtons()}
          {renderScheduling()}
        </>
      </Box>
      {renderButtons()}
      {openScheduleDialog && (
        <PriestScheduleDialog
          open={openScheduleDialog}
          handleClose={handleCloseScheduleDialog}
          schedulePriestData={schedulePriestData}
          weeklySchedules={weeklySchedules}
          isModifyOrDelete={isModifyOrDelete}
          handleDeletePriestSchedule={handleDeletePriestSchedule}
          handleSchedulePriest={handleSchedulePriest}
          handleUpdateSchedulePriest={handleUpdateSchedulePriest}
        />
      )}
    </>
  );
};

export default SchedulingScreen;
